import { Amplify, API, Auth, graphqlOperation } from "aws-amplify";
import React, { useState } from "react";
import { FieldValues } from "react-hook-form";
import {
  Device,
  SurveyConfig,
  Trigger,
  Type,
  useConfiguration,
} from "./configuration";
import { FormBuilder } from "./forms";
import awsExports from "../src/aws-exports";
export * as Survey from "./survey";
import { CognitoIdentityCredentials } from "aws-sdk";
import { SignatureV4 } from "@smithy/signature-v4";
import { Sha256 } from "@aws-crypto/sha256-js";
import { HttpRequest } from "@smithy/protocol-http";

Amplify.configure(awsExports);

export function isMobile() {
  return /Mobi|Android/i.test(navigator.userAgent);
}

export type Field = {
  name: string;
  type: Type;
  value: string | number;
};

export type Data = {
  group: String;
  type: string;
  brand: string;
  version?: string;
  name: string;
  trigger: Trigger;
  device: string;
  path: string;
  fields: Field[];
  time?: number;
};

export interface SurveyProps {
  path: string;
  survey: SurveyConfig;
  callback: () => void;
}

interface AuthProps {
  region: string;
  identityPoolId: string;
  endpoint: string;
}

export function Form({ path, survey, callback }: SurveyProps) {
  const { brand, region, endpoint, identityPoolId } = useConfiguration();
  const auth = { region, identityPoolId, endpoint };
  const [isSubmitted, setIsSubmitted] = useState(false);
  const submit = async (data: FieldValues) => {
    const surveyData: Data = {
      brand,
      group: survey.group,
      type: survey.type,
      name: survey.name,
      version: survey.version ?? undefined,
      trigger: survey.trigger,
      device: isMobile() ? Device.MOBILE : Device.DESKTOP,
      path: path,
      time: survey.time ?? undefined,
      fields: Object.keys(data).map((key) => ({
        name: survey.fields.find((field) => field.id === key)?.name ?? key,
        value: data[key],
        type:
          survey.fields.find((field) => field.name === key)?.type || Type.TEXT,
      })),
    };

    try {
      await post(surveyData, auth);
      setIsSubmitted(true); // Show confirmation after successful submission
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  return (
    <>
      {isSubmitted ? (
        <ConfirmationMessage onClose={callback} />
      ) : (
        <FormBuilder
          name={survey.name}
          fields={survey.fields}
          onSubmit={submit}
          onCancel={callback}
        />
      )}
    </>
  );
}
const ConfirmationMessage = ({ onClose }: { onClose: () => void }) => (
  <div className="flex flex-col gap-8 lg:gap-12 justify-center w-full text-lg">
    <p className="text-center">Thank you for providing feedback.</p>
    <button
      onClick={onClose}
      className="border-none py-3 px-10 text-font-cta bg-cta md:hover:bg-cta-hover active:bg-cta-hover focus:bg-cta-hover disabled:bg-cta-disabled disabled:hover:bg-cta-disabled inline-flex justify-center cursor-pointer rounded-full font-semibold whitespace-nowrap mx-auto"
    >
      Close
    </button>
  </div>
);

export async function post(data: Data, auth: AuthProps): Promise<boolean> {
  const response = await send(data, auth);

  if (response.ok) {
    return true;
  } else {
    console.error("error: ", response);
    throw new Error("Unexpected error occurred !!");
  }
}

async function send(data: Data, auth: AuthProps) {
  const { region, identityPoolId, endpoint } = auth;

  const creds = new CognitoIdentityCredentials(
    {
      IdentityPoolId: identityPoolId,
    },
    {
      region: region,
    }
  );

  await creds.getPromise();

  const signature = new SignatureV4({
    service: "execute-api",
    region: region,
    credentials: creds,
    sha256: Sha256,
  });

  const url = new URL(endpoint);

  const request = new HttpRequest({
    hostname: url.hostname,
    path: url.pathname,
    protocol: url.protocol,
    port: url.port ? Number(url.port) : undefined,
    method: "POST",
    body: JSON.stringify(data),
    query: Object.fromEntries(url.searchParams.entries()),
    fragment: url.hash,
    headers: {
      "Content-Type": "application/json",
      host: url.host,
    },
  });

  const signedRequest = await signature.sign(request);

  const response = await fetch(endpoint, {
    headers: signedRequest.headers,
    body: signedRequest.body,
    method: signedRequest.method,
  });

  return response;
}
